import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@astro/auth";
import { SocketManager, type SocketHandler, type Unsubscribe } from "./manager";

/** A realtime notification as delivered over the socket. */
export interface SocketNotification<T = unknown> {
  id: string;
  /** Server event it arrived on (see SocketProvider.notificationEvents). */
  event: string;
  payload: T;
  receivedAt: number;
}

interface SocketContextValue {
  isConnected: boolean;
  /** Subscribe to any server event; survives reconnects and login cycles. Returns unsubscribe. */
  on: (event: string, cb: SocketHandler) => Unsubscribe;
  off: (event: string, cb: SocketHandler) => void;
  /** Emit to the server. Returns false while disconnected. */
  emit: (event: string, payload?: unknown) => boolean;

  /** Latest notifications (newest first, capped). */
  notifications: SocketNotification[];
  /** True when a notification arrived since markNotificationsSeen(). */
  hasNewNotification: boolean;
  markNotificationsSeen: () => void;
  clearNotifications: () => void;

  getSocket: SocketManager["getSocket"];
}

const SocketContext = createContext<SocketContextValue | null>(null);

const MAX_NOTIFICATIONS = 50;
const DEFAULT_NOTIFICATION_EVENTS = ["notification"];

export interface SocketProviderProps {
  children: ReactNode;
  /** Override the socket server origin (defaults to VITE_SOCKET_URL → API origin). */
  url?: string;
  /** Override the socket.io mount path. */
  path?: string;
  /** Server events collected into the shared notification feed. */
  notificationEvents?: string[];
}

/**
 * App-root realtime provider, mounted once per app inside <AuthProvider>.
 *
 * Connects only while the user is authenticated — the handshake rides on the
 * shared httpOnly session cookie, so login/logout in any app of the suite
 * naturally opens/closes the socket everywhere.
 */
export function SocketProvider({
  children,
  url,
  path,
  notificationEvents = DEFAULT_NOTIFICATION_EVENTS,
}: SocketProviderProps) {
  const { status, refresh } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState<SocketNotification[]>([]);
  const [hasNewNotification, setHasNewNotification] = useState(false);

  // refresh() identity changes with auth state; the manager only needs the
  // latest one, without being torn down on every auth render.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  const [manager] = useState(
    () =>
      new SocketManager({
        url,
        path,
        onAuthError: () => refreshRef.current(),
      })
  );

  useEffect(() => manager.onConnectionChange(setIsConnected), [manager]);

  // Session lifecycle: open while authenticated, closed otherwise.
  // The disconnect is deferred one tick: React StrictMode mounts effects
  // twice in dev, and tearing the socket down synchronously between the two
  // runs closes the WebSocket mid-handshake ("WebSocket is closed before the
  // connection is established"). Reconnecting within the same tick cancels
  // the pending teardown, so one socket survives; real unmount/logout still
  // disconnects on the next tick.
  const disconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (status !== "authenticated") return;
    if (disconnectTimer.current !== null) {
      clearTimeout(disconnectTimer.current);
      disconnectTimer.current = null;
    }
    manager.connect();
    return () => {
      disconnectTimer.current = setTimeout(() => {
        disconnectTimer.current = null;
        manager.disconnect();
      }, 0);
    };
  }, [status, manager]);

  // Notification feed.
  useEffect(() => {
    const unsubs = notificationEvents.map((event) =>
      manager.on(event, (payload) => {
        setNotifications((prev) =>
          [
            {
              id: crypto.randomUUID(),
              event,
              payload,
              receivedAt: Date.now(),
            },
            ...prev,
          ].slice(0, MAX_NOTIFICATIONS)
        );
        setHasNewNotification(true);
      })
    );
    return () => unsubs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manager, notificationEvents.join("|")]);

  const on = useCallback<SocketContextValue["on"]>((event, cb) => manager.on(event, cb), [manager]);
  const off = useCallback<SocketContextValue["off"]>((event, cb) => manager.off(event, cb), [manager]);
  const emit = useCallback<SocketContextValue["emit"]>(
    (event, payload) => manager.emit(event, payload),
    [manager]
  );
  const markNotificationsSeen = useCallback(() => setHasNewNotification(false), []);
  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setHasNewNotification(false);
  }, []);

  const value = useMemo<SocketContextValue>(
    () => ({
      isConnected,
      on,
      off,
      emit,
      notifications,
      hasNewNotification,
      markNotificationsSeen,
      clearNotifications,
      getSocket: () => manager.getSocket(),
    }),
    [isConnected, on, off, emit, notifications, hasNewNotification, markNotificationsSeen, clearNotifications, manager]
  );

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

export function useSocket(): SocketContextValue {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error("useSocket must be used within <SocketProvider>");
  return ctx;
}
